/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Slider, styled, Typography } from '@mui/material';
import React, { useState } from 'react';
import { CustomTooltip } from '@opengeoweb/shared';
import type { Mark } from './utils';

const SmallFontSlider = styled(Slider)(() => ({
  '& .MuiSlider-markLabel': {
    fontSize: '80%',
  },
}));

interface DimensionSelectSliderProps {
  marks: Mark[];
  layerTitle: string;
  reverse?: boolean;
  isDisabled?: boolean;
  managedValue?: number | string;
  validSelection?: boolean;
  alertColor?: string;
  onChangeDimensionValue: (dimensionValue: string) => void;
}

const DimensionSelectSlider: React.FC<DimensionSelectSliderProps> = ({
  marks,
  layerTitle,
  reverse = false,
  isDisabled = false,
  managedValue = null!,
  validSelection = true,
  alertColor = 'red',
  onChangeDimensionValue,
}: DimensionSelectSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!marks?.length) {
    return null;
  }
  const sortedMarks = [...marks].sort((compA, compB) =>
    Number(
      typeof compA.value === 'number' &&
        typeof compB.value === 'number' &&
        compA.value - compB.value,
    ),
  );
  const adaptedMarks = reverse ? sortedMarks.reverse() : sortedMarks;

  const values = adaptedMarks.map((mark) => mark.value);
  // using index as values in slider to get constant spacing between marks
  const indexLabelMarks = adaptedMarks.map((mark, index) => {
    return { value: index, label: mark.label };
  });

  const sliderValue =
    managedValue !== null
      ? values.findIndex((value) => value === managedValue)
      : currentIndex;

  const upDownNavigation = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.metaKey ||
      event.ctrlKey
    ) {
      event.preventDefault();
    }
  };

  return (
    <>
      <Box
        data-testid="slider-dimensionSelect"
        sx={{
          height: '320px',
          margin: '10px',
          marginLeft: '-5px',
          minWidth: 120,
          fontSize: '90%',
        }}
      >
        <Box
          component="span"
          sx={{
            margin: 1,
          }}
        >
          <SmallFontSlider
            aria-labelledby="vertical-slider"
            data-testid="verticalSlider"
            disabled={isDisabled}
            marks={indexLabelMarks}
            max={indexLabelMarks.length - 1}
            min={0}
            onChange={(e, val: number | number[]): void => {
              if (typeof val === 'number') {
                onChangeDimensionValue(values[val].toString());
                if (!managedValue) {
                  setCurrentIndex(val);
                }
              }
            }}
            onKeyDown={upDownNavigation}
            orientation="vertical"
            step={null}
            style={!validSelection ? { color: alertColor } : {}}
            value={sliderValue}
          />
        </Box>
      </Box>
      <CustomTooltip title={layerTitle}>
        <Typography
          sx={{
            paddingLeft: '10px',
            maxWidth: 130,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          variant="body2"
        >
          {layerTitle}
        </Typography>
      </CustomTooltip>
    </>
  );
};

export default DimensionSelectSlider;
